import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { tr } from '../utils/i18n';
import { routePath } from '../i18n/routes';
import LanguageSwitcher from './LanguageSwitcher';

const sections = /* i18n:extract */ [
  {
    title: '1. Introduction',
    paragraphs: [
      "Bienvenue sur www.optimhub.com. Ces Conditions Générales d'Utilisation (\"CGU\") régissent votre utilisation du site web www.optimhub.com (\"Site Web\"), qui est géré par OptimHub (\"nous\", \"notre\" ou \"nos\"). En utilisant notre Site Web, vous acceptez ces CGU. Si vous n'acceptez pas ces CGU, veuillez ne pas utiliser notre Site Web."
    ]
  },
  {
    title: '2. Politique de Confidentialité',
    paragraphs: [
      'Notre Politique de Confidentialité, qui décrit comment nous collectons, utilisons et protégeons vos données personnelles, est disponible sur notre Site Web. En utilisant notre Site Web, vous acceptez les termes de la Politique de Confidentialité.'
    ]
  },
  {
    title: '3. Propriété Intellectuelle',
    paragraphs: [
      "Tout le contenu de ce Site Web, y compris le texte, les graphiques, les logos, les images et les logiciels, est la propriété d'OptimHub ou de ses fournisseurs de contenu et est protégé par les lois internationales sur le droit d'auteur et la propriété intellectuelle."
    ]
  },
  {
    title: "4. Licence d'Utilisation",
    paragraphs: [
      "(a) La permission est accordée de télécharger temporairement une copie des matériaux (informations ou logiciels) sur le site web d'OptimHub pour un usage personnel, non commercial et transitoire seulement. Il s'agit de l'octroi d'une licence, non d'un transfert de titre, et sous cette licence, vous ne pouvez pas :"
    ],
    list: [
      'modifier ou copier les matériaux ;',
      'utiliser les matériaux à des fins commerciales ou pour tout affichage public (commercial ou non commercial) ;',
      "tenter de décompiler ou d'ingénierie inverse tout logiciel contenu sur le site web d'OptimHub ;",
      "supprimer tout droit d'auteur ou autre notation propriétaire des matériaux ; ou",
      'transférer les matériaux à une autre personne ou "miroir" les matériaux sur tout autre serveur.'
    ],
    footer: [
      "(b) Cette licence se terminera automatiquement si vous violez l'une de ces restrictions et peut être résiliée par OptimHub à tout moment. À la fin de votre consultation de ces matériaux ou à la résiliation de cette licence, vous devez détruire tous les matériaux téléchargés en votre possession, qu'ils soient en format électronique ou imprimé."
    ]
  },
  {
    title: '5. Clause de Non-Responsabilité',
    paragraphs: [
      "Les matériaux sur le site web d'OptimHub sont fournis \"en l'état\". OptimHub ne donne aucune garantie, expresse ou implicite, et décline par les présentes toutes autres garanties y compris, sans limitation, les garanties implicites ou conditions de qualité marchande, d'adéquation à un usage particulier, ou de non-violation de la propriété intellectuelle ou autre violation des droits."
    ]
  },
  {
    title: '6. Limitations',
    paragraphs: [
      "En aucun cas OptimHub ou ses fournisseurs ne seront responsables de tout dommage (y compris, sans limitation, les dommages pour perte de données ou de profit, ou dus à l'interruption d'activité) découlant de l'utilisation ou de l'incapacité d'utiliser les matériaux sur le site web d'OptimHub, même si OptimHub ou un représentant autorisé d'OptimHub a été notifié oralement ou par écrit de la possibilité de tels dommages."
    ]
  },
  {
    title: '7. Précision des Matériaux',
    paragraphs: [
      "Les matériaux apparaissant sur le site web d'OptimHub pourraient inclure des erreurs techniques, typographiques ou photographiques. OptimHub ne garantit pas que les matériaux sur son site web sont exacts, complets ou actuels. OptimHub peut apporter des modifications aux matériaux contenus sur son site web à tout moment sans préavis. Cependant, OptimHub ne s'engage pas à mettre à jour les matériaux."
    ]
  },
  {
    title: '8. Liens',
    paragraphs: [
      "OptimHub n'a pas examiné tous les sites liés à son site web et n'est pas responsable du contenu de ces sites liés. L'inclusion d'un lien n'implique pas l'approbation par OptimHub du site. L'utilisation de tout site web lié est aux risques de l'utilisateur."
    ]
  },
  {
    title: '9. Modifications',
    paragraphs: [
      'OptimHub peut réviser ces CGU à tout moment sans préavis. En utilisant ce site web, vous acceptez d\'être lié par la version actuelle de ces CGU.'
    ]
  },
  {
    title: '10. Droit Applicable',
    paragraphs: [
      'Ces CGU sont régies et interprétées conformément aux lois de la France et vous vous soumettez irrévocablement à la juridiction exclusive des tribunaux de cet État.'
    ]
  },
  {
    title: '12. Hébergement',
    paragraphs: ['Le site web OptimHub est hébergé par OVH.']
  }
] /* i18n:end */;

const TermsOfService = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const l = (text) => tr(currentLanguage, text);

  const contactSection = sections.findIndex((s) => s.title === '12. Hébergement');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50" data-testid="terms-page">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <Button
              onClick={() => navigate(routePath('home', currentLanguage))}
              variant="ghost"
              size="sm"
              className="mr-4"
              data-testid="terms-back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {l('Retour')}
            </Button>
            <img
              src="https://customer-assets.emergentagent.com/job_traffic-partner/artifacts/hrx2swuq_logo2%20%281%29.png"
              alt="OptimHub"
              className="h-8 w-auto"
            />
            <div className="ml-auto">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-xl">
          <CardHeader className="text-center pb-8">
            <CardTitle className="text-3xl font-bold text-gray-900 mb-2" data-testid="terms-title">
              {l("Conditions Générales d'Utilisation")}
            </CardTitle>
            <p className="text-gray-600">OptimHub - www.optimhub.com</p>
          </CardHeader>

          <CardContent className="prose prose-lg max-w-none">
            <div className="space-y-8">
              {sections.map((section, index) => (
                <React.Fragment key={index}>
                  {index === contactSection && (
                    <section>
                      <h2 className="text-xl font-semibold text-gray-900 mb-4">{l('11. Nous Contacter')}</h2>
                      <p className="text-gray-700 leading-relaxed">
                        {l('Si vous avez des questions concernant ces CGU, veuillez nous contacter à')}{' '}
                        <a href="mailto:dpo@optimhub.com" className="text-blue-600 hover:text-blue-800">dpo@optimhub.com</a>.
                      </p>
                    </section>
                  )}
                  <section>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{l(section.title)}</h2>
                    {section.paragraphs?.map((paragraph, i) => (
                      <p key={i} className="text-gray-700 leading-relaxed mb-4">
                        {l(paragraph)}
                      </p>
                    ))}
                    {section.list && (
                      <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed">
                        {section.list.map((item, i) => (
                          <li key={i}>{l(item)}</li>
                        ))}
                      </ul>
                    )}
                    {section.footer?.map((paragraph, i) => (
                      <p key={i} className="text-gray-700 leading-relaxed mt-4">
                        {l(paragraph)}
                      </p>
                    ))}
                  </section>
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
